import React, { useState } from 'react';
import { Button, useToast } from '@crisellevaldez/speculo-ui';
import { Sparkles, Loader2 } from 'lucide-react';
import { improveText } from '../../../api/flows';

interface ImproveWithAIButtonProps {
  /** The current text to improve */
  text: string;
  /** Callback when text is improved */
  onImproved: (improvedText: string) => void;
  /** Optional callback to save the improved text (e.g., to update the flow in the database) */
  onSave?: (improvedText: string) => Promise<unknown> | void;
  /** Custom instructions for the AI (optional) */
  instructions?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Reusable AI improvement button component for flow descriptions
 * Uses the text-improver service to enhance text descriptions
 */
export const ImproveWithAIButton: React.FC<ImproveWithAIButtonProps> = ({
  text,
  onImproved,
  onSave,
  instructions = 'Make this flow description more professional, clear, and concise. Maintain the original intent and key details. Keep it suitable for an AI calling assistant to understand the context of calls.',
  disabled = false,
  className = '',
}) => {
  const { showToast } = useToast();
  const [isImproving, setIsImproving] = useState(false);

  const handleImprove = async () => {
    if (!text.trim()) {
      showToast({
        message: 'Please enter a description first',
        variant: 'error',
      });
      return;
    }

    setIsImproving(true);
    try {
      const result = await improveText(text, instructions);
      if (result.improved_text) {
        onImproved(result.improved_text);
        if (onSave) {
          await onSave(result.improved_text);
        }
        showToast({
          message: 'Description generated.',
          variant: 'success',
        });
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to improve description',
        variant: 'error',
      });
    } finally {
      setIsImproving(false);
    }
  };

  return (
    <Button
      size="sm"
      className={`gap-2 h-8 btn-ai-gradient px-3 ${className}`}
      onClick={handleImprove}
      disabled={disabled || isImproving}
    >
      {isImproving ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Improving Description...
        </>
      ) : (
        <>
          <Sparkles className="w-4 h-4" />
          Improve Description
        </>
      )}
    </Button>
  );
};

export default ImproveWithAIButton;
